import React, {useCallback, useEffect} from 'react';
import {STATUSES} from '@chekin/core';
import {NextStepDetails} from './types';

const IVSuccessTimeout = 2500;

type UseIVCompletionFlowProps = {
  getNextStepDetails: () => NextStepDetails;
  setProcessState: React.Dispatch<React.SetStateAction<STATUSES>>;
  navigateTo: (to: string) => void;
  onAllStepsCompleted: () => void;
  onAfterSuccessDelay?: (nextStep: NextStepDetails, postHandler: () => void) => boolean;
};

function useIVCompletionFlow({
  getNextStepDetails,
  setProcessState,
  navigateTo,
  onAllStepsCompleted,
  onAfterSuccessDelay,
}: UseIVCompletionFlowProps) {
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const timeout = timeoutRef.current;

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleFinish = useCallback(
    (to?: string) => {
      setProcessState(STATUSES.success);
      const nextStep = getNextStepDetails();
      if (nextStep.completed) {
        onAllStepsCompleted();
      }

      const link = to || nextStep.path;
      const postHandler = () => {
        if (link) navigateTo(link);
      };

      timeoutRef.current = setTimeout(() => {
        if (onAfterSuccessDelay?.(nextStep, postHandler)) {
          return;
        }

        postHandler();
      }, IVSuccessTimeout);
    },
    [getNextStepDetails, navigateTo, onAfterSuccessDelay, onAllStepsCompleted, setProcessState],
  );

  return {handleFinish};
}

export {useIVCompletionFlow};
